import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Building2,
  Search,
  Edit2,
  X,
  Loader2,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  Save,
  MapPin,
  Hash,
  Mail,
  Phone,
  Eye,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'active', label: 'Actif' },
  { value: 'inactive', label: 'Inactif' },
  { value: 'suspended', label: 'Suspendu' },
];

const STATUS_BADGE = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-slate-100 text-slate-500',
  suspended: 'bg-red-100 text-red-700',
  trial: 'bg-yellow-100 text-yellow-700',
};

const Structures = () => {
  const { hasNexusRole } = useAuth();
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchStructures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/structures');
      const data = Array.isArray(res.data)
        ? res.data
        : res.data.structures || res.data.items || [];
      setStructures(data);
    } catch (err) {
      setError('Impossible de charger les structures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStructures();
  }, [fetchStructures]);

  const filtered = structures.filter((s) => {
    const matchSearch =
      !search ||
      (s.nom || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.siret || '').includes(search);
    const matchStatus = !statusFilter || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const openDetail = (structure) => {
    setSelectedStructure(structure);
    setEditMode(false);
    setEditForm({
      nom: structure.nom || '',
      siret: structure.siret || '',
      adresse: structure.adresse || '',
      code_postal: structure.code_postal || '',
      ville: structure.ville || '',
      email: structure.email || '',
      telephone: structure.telephone || '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/structures/${selectedStructure.id}`, editForm);
      toast.success('Structure mise a jour');
      setEditMode(false);
      fetchStructures();
      setSelectedStructure((prev) => ({ ...prev, ...editForm }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-slate-100 rounded w-48 animate-pulse" />
          <div className="h-10 bg-slate-100 rounded w-64 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-slate-100 animate-pulse">
              <div className="w-10 h-10 bg-slate-100 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-100 rounded w-40" />
                <div className="h-3 bg-slate-100 rounded w-28" />
              </div>
              <div className="h-6 bg-slate-100 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Erreur</h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button onClick={fetchStructures} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors">
          <RefreshCw className="w-4 h-4" /> Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Structures</h1>
          <p className="text-slate-500 text-sm mt-1">{filtered.length} structure{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 w-56"
            />
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 bg-white"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Nom</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">SIRET</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Statut</th>
                <th className="text-center font-semibold text-slate-700 px-4 py-3 hidden lg:table-cell">Utilisateurs</th>
                <th className="text-center font-semibold text-slate-700 px-4 py-3 hidden lg:table-cell">Apps</th>
                <th className="text-right font-semibold text-slate-700 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((structure) => {
                const statusClass = STATUS_BADGE[structure.status] || STATUS_BADGE.inactive;
                return (
                  <tr
                    key={structure.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => openDetail(structure)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-nexus-50 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-nexus-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{structure.nom}</p>
                          <p className="text-xs text-slate-500">{structure.ville || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell font-mono text-xs">
                      {structure.siret || '--'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusClass}`}>
                        {structure.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 hidden lg:table-cell">
                      {structure.users_count ?? structure.user_count ?? structure.nb_users ?? '--'}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 hidden lg:table-cell">
                      {structure.apps_count ?? structure.nb_apps ?? '--'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(structure);
                        }}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    Aucune structure trouvee
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedStructure && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedStructure(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editMode ? 'Modifier la structure' : 'Details de la structure'}
              </h3>
              <button onClick={() => setSelectedStructure(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {editMode ? (
                <>
                  {[
                    { key: 'nom', label: 'Nom', icon: Building2 },
                    { key: 'siret', label: 'SIRET', icon: Hash },
                    { key: 'adresse', label: 'Adresse', icon: MapPin },
                    { key: 'code_postal', label: 'Code postal', icon: MapPin },
                    { key: 'ville', label: 'Ville', icon: MapPin },
                    { key: 'email', label: 'Email', icon: Mail },
                    { key: 'telephone', label: 'Telephone', icon: Phone },
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
                      <div className="relative">
                        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={editForm[key] || ''}
                          onChange={(e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }))}
                          className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                        />
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="space-y-3">
                  {[
                    { label: 'Nom', value: selectedStructure.nom },
                    { label: 'SIRET', value: selectedStructure.siret },
                    { label: 'Adresse', value: [selectedStructure.adresse, selectedStructure.code_postal, selectedStructure.ville].filter(Boolean).join(', ') },
                    { label: 'Email', value: selectedStructure.email },
                    { label: 'Telephone', value: selectedStructure.telephone },
                    { label: 'Statut', value: selectedStructure.status, badge: true },
                    { label: 'Utilisateurs', value: selectedStructure.users_count ?? selectedStructure.nb_users ?? '--' },
                    { label: 'Applications', value: selectedStructure.apps_count ?? selectedStructure.nb_apps ?? '--' },
                  ].map(({ label, value, badge }) => (
                    <div key={label} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <span className="text-sm text-slate-500">{label}</span>
                      {badge ? (
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_BADGE[value] || STATUS_BADGE.inactive}`}>
                          {value || 'active'}
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-slate-900">{value || '--'}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
              {editMode ? (
                <>
                  <button onClick={() => setEditMode(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    Annuler
                  </button>
                  <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Enregistrer
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setSelectedStructure(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    Fermer
                  </button>
                  {(hasNexusRole('super_admin') || hasNexusRole('admin_structure')) && (
                    <button onClick={() => setEditMode(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors">
                      <Edit2 className="w-4 h-4" /> Modifier
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Structures;
